// ============================================================================
// GroupPillPicker / PhaseBar / StatusChipRow / DayHeader (R18, R21, R22, R23)
// ----------------------------------------------------------------------------
// The four small shared pieces the redesign's lists and entry forms are built
// from. They are presentation only: every one takes what to draw and a click
// handler, and none of them fetches or writes (R34).
// ============================================================================
import type {CSSProperties, ReactNode} from 'react';
import React from 'react';

import {getProgramColor, type ProgramKey} from '../lib/programColors.js';
import {getReadableText} from '../lib/styles.js';

/** The fixed group palette, index = group number mod 6 (Table H, R06). */
export function groupColor(index: number): string {
  return `var(--group-${(((index % 6) + 6) % 6) + 1})`;
}

/** A 7px dot beside a group name; 10px in a record title (R06). */
export function GroupDot({color, size = 7}: {color: string; size?: number}): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      data-group-dot={String(size)}
      style={{width: size, height: size, borderRadius: '50%', background: color, flex: 'none'}}
    />
  );
}

const pillBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

export type GroupPill = {
  key: string;
  name: string;
  /** Group number; decides which of the six palette colors the dot takes. */
  index: number;
  /** The group's current phase, so the crew knows what to expect (R22). */
  meta?: string;
  /** Already entered in this session: reads "✓ added" and grays out (R22). */
  done?: boolean;
};

/**
 * A row of group pills. The selected pill fills with the PROGRAM color and
 * takes its lettering from getReadableText (R05a, R22), so broiler, layer and
 * sheep read in ink and pig and cattle in white — every one above 4.5:1.
 */
export function GroupPillPicker({
  groups,
  selected,
  onSelect,
  program,
  rootAttrs,
}: {
  groups: readonly GroupPill[];
  selected: string | null;
  onSelect: (key: string) => void;
  program: ProgramKey | string;
  rootAttrs?: Record<string, string>;
}): React.ReactElement {
  const fill = getProgramColor(program);
  const onFill = getReadableText(fill);
  return (
    <div
      role="group"
      data-group-pills={String(groups.length)}
      {...rootAttrs}
      style={{display: 'flex', flexWrap: 'wrap', gap: 8}}
    >
      {groups.map((g) => {
        const isSelected = g.key === selected;
        const style: CSSProperties = isSelected
          ? {...pillBase, border: `1px solid ${fill}`, background: fill, color: onFill}
          : g.done
            ? {...pillBase, color: 'var(--text-2)', background: 'var(--bg-page)'}
            : pillBase;
        return (
          <button
            key={g.key}
            type="button"
            className="wcf-clickable"
            aria-pressed={isSelected}
            data-group-pill={g.key}
            data-group-pill-state={isSelected ? 'selected' : g.done ? 'done' : 'idle'}
            onClick={() => onSelect(g.key)}
            style={style}
          >
            <GroupDot color={isSelected ? onFill : groupColor(g.index)} />
            {g.name}
            {g.done || g.meta ? (
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 'var(--r-pill)',
                  background: isSelected ? 'rgba(255,255,255,.22)' : 'var(--surface-2)',
                  color: isSelected ? onFill : g.done ? 'var(--ok-ink)' : 'var(--label)',
                }}
              >
                {g.done ? '✓ added' : g.meta}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export type Phase = {
  name: string;
  /** Share of the bar, any unit; segments are laid out in proportion. */
  weight: number;
  state: 'past' | 'current' | 'done';
  sub?: string;
};

/**
 * The schedule as one bar in the group's color: an earlier phase at 40% alpha,
 * the current phase solid, a finished one gray, with labels under their
 * segments and an optional Today marker (R23).
 */
export function PhaseBar({
  phases,
  color,
  todayAt,
  rootAttrs,
}: {
  phases: readonly Phase[];
  /** The group's color; the whole bar is one hue (R06). */
  color: string;
  /** 0–1 along the bar, or null for no marker. */
  todayAt?: number | null;
  rootAttrs?: Record<string, string>;
}): React.ReactElement | null {
  if (!phases.length) return null;
  return (
    <div data-phase-bar={String(phases.length)} {...rootAttrs}>
      <div
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: phases.map((p) => `${Math.max(0, p.weight)}fr`).join(' '),
          height: 14,
          borderRadius: 'var(--r-pill)',
          overflow: 'hidden',
          background: 'var(--surface-2)',
        }}
      >
        {phases.map((p) => (
          <div
            key={p.name}
            data-phase={p.name}
            data-phase-state={p.state}
            title={p.name}
            style={{
              background: p.state === 'current' ? color : p.state === 'past' ? color : 'var(--surface-2)',
              opacity: p.state === 'past' ? 0.4 : 1,
            }}
          />
        ))}
        {typeof todayAt === 'number' ? (
          <span
            aria-hidden="true"
            data-phase-today="1"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${Math.min(100, Math.max(0, todayAt * 100))}%`,
              width: 2,
              background: 'var(--ink)',
            }}
          />
        ) : null}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: phases.map((p) => `${Math.max(0, p.weight)}fr`).join(' '),
          gap: 0,
          marginTop: 6,
        }}
      >
        {phases.map((p) => (
          <div key={p.name} style={{fontSize: 12, color: 'var(--text-2)', paddingRight: 8}}>
            <div style={{fontWeight: p.state === 'current' ? 700 : 600, color: 'var(--ink)'}}>{p.name}</div>
            {p.sub ? <div>{p.sub}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export type PillOption = {value: string; label: ReactNode};

/**
 * One choice from a few, as a WRAPPING row of pills rather than the joined
 * `Segmented` bar (Q01, R18: selected is a brand outline, never a solid fill).
 * Use this where `Segmented` would be wrong: labels too long to compress into
 * equal segments, such as the four hatchery names on Add Batch. Short, evenly
 * sized choices still belong in `Segmented`.
 */
export function PillGroup({
  options,
  value,
  onChange,
  label,
  rootAttrs,
}: {
  options: readonly PillOption[];
  value: string;
  onChange: (next: string) => void;
  /** Names the group for assistive tech; the visible caption sits above it. */
  label: string;
  rootAttrs?: Record<string, string>;
}): React.ReactElement {
  return (
    <div
      role="group"
      aria-label={label}
      data-pill-group={String(options.length)}
      {...rootAttrs}
      style={{display: 'flex', flexWrap: 'wrap', gap: 8}}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            className={on ? 'wcf-clickable wcf-selected' : 'wcf-clickable'}
            aria-pressed={on}
            data-pill={o.value}
            onClick={() => {
              if (!on) onChange(o.value);
            }}
            style={{
              ...pillBase,
              padding: '9px 14px',
              fontSize: 13,
              border: `1px solid ${on ? 'var(--brand)' : 'var(--border-strong)'}`,
              color: on ? 'var(--brand)' : 'var(--ink)',
              fontWeight: on ? 700 : 600,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export type StatusChip = {key: string; label: string; count?: number};

/**
 * The chip row under a list toolbar. Selected is a brand OUTLINE, never a
 * solid fill (Q01, R18); each chip carries its count.
 */
export function StatusChipRow({
  chips,
  selected,
  onSelect,
  summary,
  rootAttrs,
}: {
  chips: readonly StatusChip[];
  selected: string | readonly string[];
  onSelect: (key: string) => void;
  /** The filtered range's summary, which sits to the right of the chips (R18). */
  summary?: ReactNode;
  rootAttrs?: Record<string, string>;
}): React.ReactElement {
  return (
    <div
      data-status-chips={String(chips.length)}
      {...rootAttrs}
      style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}
    >
      {chips.map((chip) => {
        const on = typeof selected === 'string' ? chip.key === selected : selected.includes(chip.key);
        return (
          <button
            key={chip.key}
            type="button"
            className={on ? 'wcf-clickable wcf-selected' : 'wcf-clickable'}
            aria-pressed={on}
            data-status-chip={chip.key}
            onClick={() => onSelect(chip.key)}
            style={{
              ...pillBase,
              padding: '7px 14px',
              fontSize: 13,
              border: `1px solid ${on ? 'var(--brand)' : 'var(--border-strong)'}`,
              color: on ? 'var(--brand)' : 'var(--ink)',
              fontWeight: on ? 700 : 600,
            }}
          >
            {chip.label}
            {typeof chip.count === 'number' ? (
              <span style={{color: on ? 'var(--brand)' : 'var(--label)', fontWeight: 700}}>{chip.count}</span>
            ) : null}
          </button>
        );
      })}
      {summary ? (
        <span style={{marginLeft: 'auto', fontSize: 13, color: 'var(--text-2)'}} data-chip-summary="1">
          {summary}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The light header a day-grouped list puts above its rows: "Today · Oct 21 —
 * 6 reports · 240 lb" (R21).
 */
export function DayHeader({
  day,
  summary,
  rootAttrs,
}: {
  day: string;
  summary?: ReactNode;
  rootAttrs?: Record<string, string>;
}): React.ReactElement {
  return (
    <div
      data-day-header={day}
      {...rootAttrs}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        padding: '10px 14px',
        background: 'var(--bg-page)',
        borderBottom: '1px solid var(--hairline)',
        fontSize: 13,
      }}
    >
      <span style={{fontWeight: 800, color: 'var(--ink)'}}>{day}</span>
      {summary ? <span style={{color: 'var(--text-2)'}}>{summary}</span> : null}
    </div>
  );
}

/**
 * A row's comment: a full-width italic line on the warm tint, under that row's
 * data. A row without one stays a single line (R21).
 */
export function RowComment({children}: {children: ReactNode}): React.ReactElement {
  return (
    <div
      data-row-comment="1"
      style={{
        gridColumn: '1 / -1',
        background: 'var(--data-soft)',
        color: 'var(--ink)',
        fontStyle: 'italic',
        fontSize: 13,
        padding: '8px 14px',
      }}
    >
      {children}
    </div>
  );
}
